import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    IconBuildingBank,
    IconCar,
    IconCopy,
    IconFileSpreadsheet,
    IconFileTypePdf,
    IconHome,
    IconPrinter,
    IconRefresh,
    IconSchool,
    IconShare2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    copyTextToClipboard,
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';
import {
    LOAN_PRESETS,
    calculateEmi,
    tenureToMonths,
    validateEmiInputs,
} from './emiCalculatorEngine';
import {
    buildEmiCalculatorShareUrl,
    parseEmiCalculatorShareParams,
} from './emiCalculatorShare';
import { downloadEmiCalculatorPdf } from './generateEmiCalculatorExport';

const PAGE_SIZE = 12;
const TOOL_PATH = '/tools/general/emi-calculator';

const inputClass = 'w-full rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 text-[15px] font-semibold text-[#3D3833] outline-none transition-colors focus:border-orange-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';
const inputErrorClass = 'border-red-500 dark:border-red-500';

function bnToAr(str) {
    return String(str).replace(/[০-৯]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x09e6 + 48));
}

function formatNumber(value, lang, decimals = 2) {
    const num = typeof value === 'number' ? value : Number(value);
    const str = Number.isInteger(num) && decimals === 0 ? String(num) : num.toFixed(decimals);
    if (lang !== 'bn') return str;
    return toBnDigits(str);
}

function formatCurrency(amount, lang) {
    const formatted = new Intl.NumberFormat(lang === 'bn' ? 'bn-BD' : 'en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
    const withBnDigits = lang === 'bn' ? toBnDigits(formatted) : formatted;
    return `৳${withBnDigits}`;
}

function LocalizedNumberInput({
    value,
    onChange,
    lang,
    allowDecimal = false,
    prefix = '',
    placeholder = '',
    error,
    id,
}) {
    const isBn = lang === 'bn';

    const fmt = (v) => {
        if (v === '' || v === null || v === undefined) return '';
        const s = String(v);
        return isBn ? toBnDigits(s) : s;
    };

    const [display, setDisplay] = useState(() => fmt(value));
    const prevValueRef = useRef(value);

    useEffect(() => {
        if (prevValueRef.current !== value) {
            prevValueRef.current = value;
            setDisplay(fmt(value));
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setDisplay((prev) => {
            const ar = bnToAr(prev);
            return isBn ? toBnDigits(ar) : ar;
        });
    }, [isBn]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const raw = bnToAr(e.target.value);
        const cleaned = allowDecimal
            ? raw.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
            : raw.replace(/\D/g, '');

        setDisplay(isBn ? toBnDigits(cleaned) : cleaned);

        if (cleaned === '' || cleaned === '.') {
            if (value !== '') onChange('');
        } else if (!cleaned.endsWith('.')) {
            const n = Number(cleaned);
            if (!Number.isNaN(n)) onChange(n);
        }
    };

    return (
        <div className="relative">
            {prefix ? (
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 select-none text-[13px] font-bold text-[#6B6B75] dark:text-zinc-500">
                    {prefix.trim()}
                </span>
            ) : null}
            <input
                id={id}
                type="text"
                inputMode={allowDecimal ? 'decimal' : 'numeric'}
                value={display}
                onChange={handleChange}
                placeholder={placeholder}
                className={`${inputClass} ${prefix ? 'pl-9' : ''} ${error ? inputErrorClass : ''}`}
            />
            {error ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400" role="alert">{error}</p>
            ) : null}
        </div>
    );
}

function UnitToggle({ value, onChange, yearsLabel, monthsLabel }) {
    return (
        <div className="inline-flex gap-0.5 rounded-lg border border-[#E8E8EE] bg-[#F7F7F9] p-0.5 dark:border-zinc-700 dark:bg-zinc-800">
            {[
                { id: 'years', label: yearsLabel },
                { id: 'months', label: monthsLabel },
            ].map(({ id, label }) => {
                const active = value === id;
                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onChange(id)}
                        className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                            active
                                ? 'bg-primary text-white'
                                : 'text-[#6B6B75] hover:text-[#3A3A42] dark:text-zinc-400 dark:hover:text-zinc-200'
                        }`}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

function ExportPill({ children, outline }) {
    return (
        <span
            className={`inline-block whitespace-nowrap rounded-lg border-[1.5px] px-3 py-1.5 text-[12px] font-semibold leading-snug text-orange-800 dark:text-orange-300 ${
                outline
                    ? 'border-orange-200 bg-white dark:border-orange-900/40 dark:bg-zinc-900'
                    : 'border-orange-200 bg-orange-50 dark:border-orange-900/40 dark:bg-orange-950/20'
            }`}
        >
            {children}
        </span>
    );
}

function ExportStatCard({ label, value, highlight }) {
    return (
        <div
            className={`min-h-16 min-w-0 rounded-[10px] border-[1.5px] bg-white px-3 py-3.5 dark:bg-zinc-900 ${
                highlight ? 'border-orange-200 dark:border-orange-900/40' : 'border-[#E8E8EE] dark:border-zinc-700'
            }`}
        >
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[#6B6B75] dark:text-zinc-500">
                {label}
            </p>
            <p
                className={`break-words text-[18px] font-extrabold tabular-nums leading-tight sm:text-[22px] ${
                    highlight ? 'text-primary' : 'text-[#1B1B1F] dark:text-zinc-100'
                }`}
            >
                {value}
            </p>
        </div>
    );
}

function ExportInfoRow({ label, value, last }) {
    return (
        <div
            className={`flex items-center justify-between gap-3 rounded-lg border border-[#E8E8EE] bg-white px-3.5 py-2.5 dark:border-zinc-700 dark:bg-zinc-900 ${
                last ? '' : 'mb-2'
            }`}
        >
            <span className="text-[12px] font-medium text-[#6B6B75] dark:text-zinc-500">{label}</span>
            <span className="text-right text-[13px] font-semibold text-[#1B1B1F] dark:text-zinc-100">{value}</span>
        </div>
    );
}

function ExportSectionTitle({ children }) {
    return (
        <div className="mb-2.5 flex items-center gap-2">
            <span className="h-4 w-[3px] flex-shrink-0 rounded-sm bg-primary" />
            <p className="text-[14px] font-bold text-orange-800 dark:text-orange-300">{children}</p>
        </div>
    );
}

function InfoCard({ title, body }) {
    return (
        <div className="h-full rounded-[10px] border-[1.5px] border-orange-200 bg-white px-4 py-3.5 dark:border-orange-900/40 dark:bg-zinc-900">
            <p className="mb-1.5 text-[13px] font-bold text-orange-800 dark:text-orange-300">{title}</p>
            <p className="text-[12px] leading-relaxed text-[#6B6B75] dark:text-zinc-500">{body}</p>
        </div>
    );
}

function RepaymentDonutChart({ principal, interest, labels }) {
    const total = principal + interest;
    const principalPct = total > 0 ? (principal / total) * 100 : 50;
    const size = 200;
    const strokeWidth = 36;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const principalLength = (principalPct / 100) * circumference;
    const interestLength = circumference - principalLength;

    return (
        <div className="mx-auto max-w-[240px]" aria-label={labels.title}>
            <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto w-full" role="img">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#FFCB9A"
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#F77B1C"
                    strokeWidth={strokeWidth}
                    strokeDasharray={`${principalLength} ${interestLength}`}
                    strokeLinecap="butt"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-4 text-[11px] font-semibold text-[#3A3A42] dark:text-zinc-300">
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-primary" />
                    {labels.principal}
                    {total > 0 ? ` (${formatNumber(principalPct, 'en', 1)}%)` : ''}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-[#FFCB9A]" />
                    {labels.interest}
                    {total > 0 ? ` (${formatNumber(100 - principalPct, 'en', 1)}%)` : ''}
                </span>
            </div>
        </div>
    );
}

function SchedulePagination({ page, totalPages, onChange, lang }) {
    if (totalPages <= 1) return null;

    return (
        <div className="mt-3 flex items-center justify-center gap-2">
            <button
                type="button"
                onClick={() => onChange(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-[#E8E8EE] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            >
                {lang === 'bn' ? '‹' : 'Prev'}
            </button>
            <span className="min-w-[4rem] text-center text-[12px] font-semibold tabular-nums text-[#6B6B75] dark:text-zinc-500">
                {formatNumber(page, lang, 0)} / {formatNumber(totalPages, lang, 0)}
            </span>
            <button
                type="button"
                onClick={() => onChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-[#E8E8EE] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            >
                {lang === 'bn' ? '›' : 'Next'}
            </button>
        </div>
    );
}

function MainEmiDisplay({ result, lang, t }) {
    if (!result?.ok) return null;

    return (
        <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-6 text-center dark:border-orange-900/40 dark:bg-orange-950/20">
            <p className="mb-5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#6B6B75] dark:text-zinc-500">
                {t('emiCalculator.monthlyEmi')}
            </p>
            <p className="text-[36px] font-black leading-none text-primary sm:text-[40px]">
                {formatCurrency(result.emi, lang)}
            </p>
        </div>
    );
}

export default function EmiCalculatorGeneral() {
    const { t, currentLanguage } = useTranslation();
    const lang = currentLanguage === 'en' ? 'en' : 'bn';
    const isBn = lang === 'bn';
    const location = useLocation();
    const usageRecordedRef = useRef(false);
    const exportRef = useRef(null);
    const [exporting, setExporting] = useState(null);

    const [principal, setPrincipal] = useState('');
    const [rate, setRate] = useState('');
    const [tenure, setTenure] = useState('');
    const [unit, setUnit] = useState('years');
    const [scheduleSearch, setScheduleSearch] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        const search = location.search;
        if (!search) return;
        const parsed = parseEmiCalculatorShareParams(search);
        if (parsed.principal) setPrincipal(parsed.principal);
        if (parsed.rate !== null) setRate(parsed.rate);
        if (parsed.tenure) setTenure(parsed.tenure);
        setUnit(parsed.unit);
    }, [location.search]);

    const isBlank = (value) => value === '' || value === null || value === undefined;
    const isFormEmpty = isBlank(principal) && isBlank(rate) && isBlank(tenure);

    const principalNum = isBlank(principal) ? NaN : Number(principal);
    const rateNum = isBlank(rate) ? NaN : Number(rate);

    const tenureMonths = useMemo(() => {
        if (isBlank(tenure)) return 0;
        return tenureToMonths(Number(tenure), unit);
    }, [tenure, unit]);

    const validation = useMemo(
        () => validateEmiInputs(principalNum, rateNum, tenureMonths),
        [principalNum, rateNum, tenureMonths],
    );

    const result = useMemo(() => {
        if (!validation.valid) return null;
        return calculateEmi(principalNum, rateNum, tenureMonths);
    }, [validation, principalNum, rateNum, tenureMonths]);

    const inputError = useMemo(() => {
        if (validation.valid || isFormEmpty) return null;
        return t(`emiCalculator.errors.${validation.error}`);
    }, [validation, isFormEmpty, t]);

    const durationLabel = useMemo(() => {
        if (!result?.ok) return '';
        return unit === 'years'
            ? `${formatNumber(tenure, lang, 0)} ${t('emiCalculator.yearsUnit')} (${formatNumber(result.tenureMonths, lang, 0)} ${t('emiCalculator.monthsUnit')})`
            : `${formatNumber(result.tenureMonths, lang, 0)} ${t('emiCalculator.monthsUnit')}`;
    }, [result, unit, tenure, lang, t]);

    const filteredSchedule = useMemo(() => {
        if (!result?.ok) return [];
        const q = scheduleSearch.trim();
        if (!q) return result.schedule;
        return result.schedule.filter((row) => String(row.month).includes(q));
    }, [result, scheduleSearch]);

    const totalPages = Math.max(1, Math.ceil(filteredSchedule.length / PAGE_SIZE));
    const pagedSchedule = filteredSchedule.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => {
        setPage(1);
    }, [scheduleSearch, principal, rate, tenure, unit]);

    const applyPreset = (key) => {
        const preset = LOAN_PRESETS[key];
        setPrincipal(preset.principal);
        setRate(preset.rate);
        setTenure(preset.tenure);
        setUnit(preset.unit);
    };

    const selectedPreset = useMemo(() => {
        if (isFormEmpty) return null;
        for (const [key, preset] of Object.entries(LOAN_PRESETS)) {
            if (
                principalNum === preset.principal
                && rateNum === preset.rate
                && Number(tenure) === preset.tenure
                && unit === preset.unit
            ) {
                return key;
            }
        }
        return null;
    }, [isFormEmpty, principalNum, rateNum, tenure, unit]);

    const recordUsageOnce = useCallback(() => {
        if (usageRecordedRef.current) return;
        usageRecordedRef.current = true;
        recordToolUsage('general/emi-calculator', { category: 'general' });
    }, []);

    useEffect(() => {
        if (result?.ok) {
            recordUsageOnce();
        }
    }, [result, recordUsageOnce]);

    const resultsText = useMemo(() => {
        if (!result?.ok) return '';
        return [
            `${t('emiCalculator.monthlyEmi')}: ${formatCurrency(result.emi, lang)}`,
            `${t('emiCalculator.principalAmount')}: ${formatCurrency(result.principal, lang)}`,
            `${t('emiCalculator.totalInterest')}: ${formatCurrency(result.totalInterest, lang)}`,
            `${t('emiCalculator.totalRepayment')}: ${formatCurrency(result.totalPayment, lang)}`,
            `${t('emiCalculator.breakdownInterestPct')}: ${formatNumber(result.interestPercent, lang, 1)}%`,
            `${t('emiCalculator.breakdownDuration')}: ${durationLabel}`,
            `${t('emiCalculator.interestRate')}: ${formatNumber(rateNum, lang, 2)}%`,
        ].join('\n');
    }, [result, lang, t, durationLabel, rateNum]);

    const handleCopy = async () => {
        if (!resultsText) return;
        const ok = copyTextToClipboard(resultsText);
        if (ok) {
            notifications.show({ title: t('common.success'), message: t('emiCalculator.copied'), color: 'green' });
            return;
        }
        try {
            await navigator.clipboard.writeText(resultsText);
            notifications.show({ title: t('common.success'), message: t('emiCalculator.copied'), color: 'green' });
        } catch {
            notifications.show({ title: t('common.error'), message: t('emiCalculator.copyFailed'), color: 'red' });
        }
    };

    const handleShare = async () => {
        if (!validation.valid) {
            notifications.show({ title: t('common.error'), message: t('emiCalculator.shareRequiresInput'), color: 'yellow' });
            return;
        }

        const url = buildEmiCalculatorShareUrl(window.location.origin, TOOL_PATH, {
            principal: principalNum,
            rate: rateNum,
            tenure: isBlank(tenure) ? undefined : Number(tenure),
            unit,
        });

        if (navigator.share) {
            try {
                await navigator.share({ title: t('emiCalculator.shareTitle'), text: t('emiCalculator.shareText'), url });
                notifications.show({ title: t('common.success'), message: t('emiCalculator.shared'), color: 'green' });
                return;
            } catch (err) {
                if (err?.name === 'AbortError') return;
            }
        }

        const copied = copyTextToClipboard(url);
        if (copied) {
            notifications.show({ title: t('common.success'), message: t('emiCalculator.linkCopied'), color: 'green' });
        } else {
            notifications.show({ title: t('common.error'), message: t('emiCalculator.shareFailed'), color: 'red' });
        }
    };

    const handleExportCsv = () => {
        if (!result?.ok) return;
        const header = [
            t('emiCalculator.scheduleMonth'),
            t('emiCalculator.scheduleEmi'),
            t('emiCalculator.schedulePrincipal'),
            t('emiCalculator.scheduleInterest'),
            t('emiCalculator.scheduleBalance'),
        ];
        const rows = result.schedule.map((row) =>
            [row.month, row.emi.toFixed(2), row.principalPaid.toFixed(2), row.interestPaid.toFixed(2), row.balance.toFixed(2)].join(','),
        );
        const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
        downloadBlob(blob, `emi-schedule-${Date.now()}.csv`);
        notifications.show({ title: t('common.success'), message: t('emiCalculator.csvDownloaded'), color: 'green' });
    };

    const buildScheduleRowsHtml = useCallback(() => {
        if (!result?.ok) return '';
        return result.schedule
            .map(
                (row, i) => `
                    <tr class="${i % 2 === 1 ? 'bg-[#FAFAFC]' : 'bg-white'}">
                        <td class="border-t border-[#E8E8EE] px-3 py-2 text-center">${formatNumber(row.month, lang, 0)}</td>
                        <td class="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums">${formatCurrency(row.emi, lang)}</td>
                        <td class="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums">${formatCurrency(row.principalPaid, lang)}</td>
                        <td class="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums">${formatCurrency(row.interestPaid, lang)}</td>
                        <td class="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums">${formatCurrency(row.balance, lang)}</td>
                    </tr>
                `,
            )
            .join('');
    }, [result, lang]);

    const handleDownloadPdf = async () => {
        if (!exportRef.current || !result?.ok) return;
        setExporting('pdf');
        try {
            await downloadEmiCalculatorPdf(
                exportRef.current,
                `emi-calculator-${Date.now()}.pdf`,
                { scheduleRowsHtml: buildScheduleRowsHtml() },
            );
            notifications.show({ title: t('common.success'), message: t('emiCalculator.pdfDownloaded'), color: 'green' });
        } catch {
            notifications.show({ title: t('common.error'), message: t('emiCalculator.exportFailed'), color: 'red' });
        } finally {
            setExporting(null);
        }
    };

    const handlePrint = useCallback(() => {
        if (!result?.ok) return;

        recordUsageOnce();
        const rows = result.schedule
            .map(
                (row) =>
                    `<tr><td>${formatNumber(row.month, lang, 0)}</td><td>${formatCurrency(row.emi, lang)}</td><td>${formatCurrency(row.principalPaid, lang)}</td><td>${formatCurrency(row.interestPaid, lang)}</td><td>${formatCurrency(row.balance, lang)}</td></tr>`,
            )
            .join('');

        const html = `<!DOCTYPE html><html><head><title>${t('emiCalculator.scheduleTitle')}</title>
      <style>body{font-family:sans-serif;padding:24px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:right}th{background:#FFF4E6}td:first-child,th:first-child{text-align:center}</style>
      </head><body><h2>${t('emiCalculator.scheduleTitle')}</h2><table><thead><tr>
      <th>${t('emiCalculator.scheduleMonth')}</th><th>${t('emiCalculator.scheduleEmi')}</th><th>${t('emiCalculator.schedulePrincipal')}</th><th>${t('emiCalculator.scheduleInterest')}</th><th>${t('emiCalculator.scheduleBalance')}</th>
      </tr></thead><tbody>${rows}</tbody></table></body></html>`;

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDoc = frameWindow?.document;
        if (!frameDoc || !frameWindow) {
            iframe.remove();
            return;
        }

        frameDoc.open();
        frameDoc.write(html);
        frameDoc.close();

        const cleanup = () => iframe.remove();
        frameWindow.addEventListener('afterprint', cleanup, { once: true });
        setTimeout(cleanup, 2000);

        frameWindow.focus();
        frameWindow.print();
    }, [result, lang, t]);

    const handleReset = () => {
        setPrincipal('');
        setRate('');
        setTenure('');
        setUnit('years');
        setScheduleSearch('');
        setPage(1);
        usageRecordedRef.current = false;
        window.history.replaceState(null, '', TOOL_PATH);
    };

    const presetButtons = [
        { key: 'home', label: t('emiCalculator.presetHome'), icon: IconHome },
        { key: 'car', label: t('emiCalculator.presetCar'), icon: IconCar },
        { key: 'personal', label: t('emiCalculator.presetPersonal'), icon: IconBuildingBank },
        { key: 'education', label: t('emiCalculator.presetEducation'), icon: IconSchool },
    ];

    const hasResult = Boolean(result?.ok);
    const hasInput = !isFormEmpty;
    const hasError = Boolean(inputError);

    const status = deriveToolStatus({
        hasInput,
        canRun: validation.valid,
        hasResult,
        hasError,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('emiCalculator.statusReady'),
        success: t('emiCalculator.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`emiCalculator.howTo.step${i}`)),
        [t],
    );

    return (
        <ToolPageLayout
            slug="general/emi-calculator"
            subtitleKey="emiCalculator.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('emiCalculator.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasInput ? handleReset : undefined}
            resetLabel={t('emiCalculator.reset')}
            controls={(
                <div className="space-y-4">
                    <div>
                        <label htmlFor="loan-amount" className="mb-1 block text-[12px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('emiCalculator.loanAmount')}
                            <span className="text-red-600"> *</span>
                        </label>
                        {!inputError || validation.error !== 'invalidPrincipal' ? (
                            <p className="mb-2 text-[11px] text-[#6B635C] dark:text-zinc-500">{t('emiCalculator.loanAmountHint')}</p>
                        ) : null}
                        <LocalizedNumberInput
                            id="loan-amount"
                            value={principal}
                            onChange={setPrincipal}
                            lang={lang}
                            prefix="৳"
                            placeholder={t('emiCalculator.loanAmountPlaceholder')}
                            error={inputError && validation.error === 'invalidPrincipal' ? inputError : undefined}
                        />
                    </div>

                    <div>
                        <label htmlFor="interest-rate" className="mb-1 block text-[12px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('emiCalculator.interestRate')}
                            <span className="text-red-600"> *</span>
                        </label>
                        {!inputError || validation.error !== 'invalidRate' ? (
                            <p className="mb-2 text-[11px] text-[#6B635C] dark:text-zinc-500">{t('emiCalculator.interestRateHint')}</p>
                        ) : null}
                        <LocalizedNumberInput
                            id="interest-rate"
                            value={rate}
                            onChange={setRate}
                            lang={lang}
                            allowDecimal
                            placeholder={isBn ? '০' : '0'}
                            error={inputError && validation.error === 'invalidRate' ? inputError : undefined}
                        />
                    </div>

                    <div>
                        <div className="mb-1 flex items-center justify-between gap-2">
                            <label htmlFor="tenure" className="text-[12px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('emiCalculator.tenure')}
                                <span className="text-red-600"> *</span>
                            </label>
                            <UnitToggle
                                value={unit}
                                onChange={setUnit}
                                yearsLabel={t('emiCalculator.tenureYears')}
                                monthsLabel={t('emiCalculator.tenureMonths')}
                            />
                        </div>
                        {!inputError || validation.error !== 'invalidTenure' ? (
                            <p className="mb-2 text-[11px] text-[#6B635C] dark:text-zinc-500">{t('emiCalculator.tenureHint')}</p>
                        ) : null}
                        <LocalizedNumberInput
                            id="tenure"
                            value={tenure}
                            onChange={setTenure}
                            lang={lang}
                            placeholder={isBn ? '০' : '0'}
                            error={inputError && validation.error === 'invalidTenure' ? inputError : undefined}
                        />
                    </div>

                    <div>
                        <p className="mb-2 text-[11px] font-semibold text-[#6B6B75] dark:text-zinc-500">
                            {t('emiCalculator.presets')}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {presetButtons.map(({ key, label, icon: Icon }) => {
                                const isSelected = selectedPreset === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        aria-pressed={isSelected}
                                        onClick={() => applyPreset(key)}
                                        className={`inline-flex items-center gap-1.5 rounded-lg border-[1.5px] px-2.5 py-1.5 text-[11px] font-semibold transition-all ${
                                            isSelected
                                                ? 'border-primary bg-primary text-white shadow-sm'
                                                : 'border-orange-200 bg-orange-50 text-orange-800 hover:border-orange-300 dark:border-orange-900/40 dark:bg-orange-950/20 dark:text-orange-300'
                                        }`}
                                    >
                                        <Icon size={12} stroke={1.8} />
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            settings={validation.valid ? (
                <div className="flex flex-col gap-2">
                    {hasResult ? (
                        <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-[11px] font-semibold tabular-nums text-orange-800 dark:border-orange-900/40 dark:bg-orange-950/20 dark:text-orange-300">
                            <span>{formatCurrency(result.emi, lang)}</span>
                            <span className="text-[10px] font-medium text-[#6B6B75] dark:text-zinc-500">
                                {t('emiCalculator.monthlyEmi')}
                            </span>
                        </div>
                    ) : null}

                    <button
                        type="button"
                        onClick={handleReset}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] font-semibold text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400"
                    >
                        <IconRefresh size={15} stroke={1.8} />
                        {t('emiCalculator.reset')}
                    </button>

                    <button
                        type="button"
                        onClick={handleCopy}
                        disabled={!resultsText}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-primary to-[#EA6B0E] px-4 py-3 text-[14px] font-semibold text-white shadow-sm transition-opacity disabled:cursor-not-allowed disabled:from-zinc-400 disabled:to-zinc-400"
                    >
                        <IconCopy size={15} stroke={1.9} />
                        {t('emiCalculator.copyResults')}
                    </button>

                    <ToolCard title={t('emiCalculator.exportShareTitle')}>
                        <div className="grid grid-cols-2 gap-1.5">
                            <button
                                type="button"
                                onClick={handleDownloadPdf}
                                disabled={!hasResult || exporting !== null}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconFileTypePdf size={15} className="text-primary" stroke={1.8} />
                                {exporting === 'pdf' ? t('emiCalculator.downloading') : t('emiCalculator.downloadPdf')}
                            </button>
                            <button
                                type="button"
                                onClick={handleExportCsv}
                                disabled={!hasResult || exporting !== null}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconFileSpreadsheet size={15} className="text-primary" stroke={1.8} />
                                {t('emiCalculator.exportCsv')}
                            </button>
                            <button
                                type="button"
                                onClick={handlePrint}
                                disabled={!hasResult || exporting !== null}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconPrinter size={15} className="text-primary" stroke={1.8} />
                                {t('emiCalculator.printSchedule')}
                            </button>
                            <button
                                type="button"
                                onClick={handleShare}
                                disabled={!validation.valid || exporting !== null}
                                className="flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-[10px] border border-[#E8E8EE] bg-[#F7F7F9] px-1.5 py-2.5 text-[10px] font-semibold leading-tight text-[#3A3A42] transition-colors hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                            >
                                <IconShare2 size={15} className="text-primary" stroke={1.8} />
                                {t('emiCalculator.share')}
                            </button>
                        </div>
                    </ToolCard>
                </div>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('emiCalculator.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('emiCalculator.enterDetails')}
                    idleHint={t('emiCalculator.previewEmptyHint')}
                    showIdle={!hasResult}
                    tall
                    fill
                    flushContent
                >
                    {inputError && !hasResult ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400">
                            {inputError}
                        </div>
                    ) : null}
                    {hasResult ? (
                        <div
                            ref={exportRef}
                            data-emi-calc-export
                            className="min-h-0 flex-1 space-y-4 overflow-y-auto rounded-lg bg-[#FBFAF9] p-3 dark:bg-zinc-950 sm:p-4"
                        >
                            <div className="rounded-xl border-[1.5px] border-orange-200 bg-orange-50 p-3 dark:border-orange-900/40 dark:bg-orange-950/20 sm:p-4">
                                <div className="flex flex-col gap-3">
                                    <div>
                                        <p className="text-[18px] font-extrabold leading-tight text-orange-800 dark:text-orange-300 sm:text-[20px]">
                                            {t('emiCalculator.title')}
                                        </p>
                                        <p className="mt-1 text-[12px] text-[#6B6B75] dark:text-zinc-500">SohozKaj</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <ExportPill>
                                            {t('emiCalculator.interestRate')}: {formatNumber(rateNum, lang, 2)}%
                                        </ExportPill>
                                        <ExportPill outline>
                                            {t('emiCalculator.breakdownDuration')}: {durationLabel}
                                        </ExportPill>
                                    </div>
                                </div>
                            </div>

                            <MainEmiDisplay result={result} lang={lang} t={t} />

                            <div className="grid grid-cols-1 gap-2.5">
                                <ExportStatCard label={t('emiCalculator.totalInterest')} value={formatCurrency(result.totalInterest, lang)} />
                                <ExportStatCard label={t('emiCalculator.totalRepayment')} value={formatCurrency(result.totalPayment, lang)} highlight />
                                <ExportStatCard label={t('emiCalculator.principalAmount')} value={formatCurrency(result.principal, lang)} />
                            </div>

                            <div className="h-px bg-[#E8E8EE] dark:bg-zinc-800" />

                            <div className="space-y-4">
                                <div>
                                    <ExportSectionTitle>{t('emiCalculator.breakdownTitle')}</ExportSectionTitle>
                                    <div className="space-y-2">
                                        <ExportInfoRow label={t('emiCalculator.breakdownPrincipal')} value={formatCurrency(result.principal, lang)} />
                                        <ExportInfoRow label={t('emiCalculator.breakdownInterest')} value={formatCurrency(result.totalInterest, lang)} />
                                        <ExportInfoRow
                                            label={t('emiCalculator.breakdownInterestPct')}
                                            value={`${formatNumber(result.interestPercent, lang, 1)}%`}
                                        />
                                        <ExportInfoRow label={t('emiCalculator.breakdownDuration')} value={durationLabel} last />
                                    </div>
                                </div>

                                <div>
                                    <ExportSectionTitle>{t('emiCalculator.chartTitle')}</ExportSectionTitle>
                                    <div className="rounded-[10px] border border-[#E8E8EE] bg-white px-3 py-3 dark:border-zinc-700 dark:bg-zinc-900">
                                        <RepaymentDonutChart
                                            principal={result.principal}
                                            interest={result.totalInterest}
                                            labels={{
                                                title: t('emiCalculator.chartTitle'),
                                                principal: t('emiCalculator.chartPrincipal'),
                                                interest: t('emiCalculator.chartInterest'),
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="h-px bg-[#E8E8EE] dark:bg-zinc-800" />

                            <div>
                                <ExportSectionTitle>{t('emiCalculator.scheduleTitle')}</ExportSectionTitle>
                                <input
                                    type="text"
                                    value={isBn ? toBnDigits(scheduleSearch) : scheduleSearch}
                                    onChange={(e) => setScheduleSearch(bnToAr(e.target.value))}
                                    placeholder={t('emiCalculator.scheduleSearch')}
                                    className={`${inputClass} mb-3 text-[14px]`}
                                    data-export-hide
                                />
                                <div className="overflow-x-auto rounded-xl border border-[#E8E8EE] dark:border-zinc-700">
                                    <table className="w-full min-w-[520px] border-collapse text-[12px]">
                                        <thead>
                                            <tr className="border-b border-[#E8E8EE] bg-orange-50 dark:border-zinc-700 dark:bg-orange-950/20">
                                                <th className="px-3 py-2.5 text-center font-semibold text-orange-800 dark:text-orange-300">
                                                    {t('emiCalculator.scheduleMonth')}
                                                </th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-orange-800 dark:text-orange-300">
                                                    {t('emiCalculator.scheduleEmi')}
                                                </th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-orange-800 dark:text-orange-300">
                                                    {t('emiCalculator.schedulePrincipal')}
                                                </th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-orange-800 dark:text-orange-300">
                                                    {t('emiCalculator.scheduleInterest')}
                                                </th>
                                                <th className="px-3 py-2.5 text-right font-semibold text-orange-800 dark:text-orange-300">
                                                    {t('emiCalculator.scheduleBalance')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody data-export-schedule-body>
                                            {pagedSchedule.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} className="px-3 py-6 text-center text-[12px] text-[#6B6B75] dark:text-zinc-500">
                                                        {t('emiCalculator.scheduleEmpty')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                pagedSchedule.map((row, i) => (
                                                    <tr
                                                        key={row.month}
                                                        className={i % 2 === 1 ? 'bg-[#FAFAFC] dark:bg-zinc-800/50' : 'bg-white dark:bg-zinc-900'}
                                                    >
                                                        <td className="border-t border-[#E8E8EE] px-3 py-2 text-center dark:border-zinc-700">
                                                            {formatNumber(row.month, lang, 0)}
                                                        </td>
                                                        <td className="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums dark:border-zinc-700">
                                                            {formatCurrency(row.emi, lang)}
                                                        </td>
                                                        <td className="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums dark:border-zinc-700">
                                                            {formatCurrency(row.principalPaid, lang)}
                                                        </td>
                                                        <td className="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums dark:border-zinc-700">
                                                            {formatCurrency(row.interestPaid, lang)}
                                                        </td>
                                                        <td className="border-t border-[#E8E8EE] px-3 py-2 text-right tabular-nums dark:border-zinc-700">
                                                            {formatCurrency(row.balance, lang)}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div data-export-hide>
                                    <SchedulePagination page={page} totalPages={totalPages} onChange={setPage} lang={lang} />
                                </div>
                            </div>

                            <div className="h-px bg-[#E8E8EE] dark:bg-zinc-800" />

                            <div>
                                <ExportSectionTitle>{t('emiCalculator.insightsTitle')}</ExportSectionTitle>
                                <div className="grid grid-cols-1 gap-3">
                                    <InfoCard title={t('emiCalculator.insightEmiTitle')} body={t('emiCalculator.insightEmiBody')} />
                                    <InfoCard title={t('emiCalculator.insightAffectsTitle')} body={t('emiCalculator.insightAffectsBody')} />
                                    <InfoCard title={t('emiCalculator.insightReduceTitle')} body={t('emiCalculator.insightReduceBody')} />
                                    <InfoCard title={t('emiCalculator.insightTenureTitle')} body={t('emiCalculator.insightTenureBody')} />
                                    <InfoCard title={t('emiCalculator.formulaTitle')} body={t('emiCalculator.formulaBody')} />
                                    <InfoCard title={t('emiCalculator.tipsTitle')} body={t('emiCalculator.tipsBody')} />
                                    <InfoCard title={t('emiCalculator.benefitsTitle')} body={t('emiCalculator.benefitsBody')} />
                                </div>
                            </div>

                            <p className="text-center text-[11px] text-[#9ca3af] dark:text-zinc-600">sohozkaj.com</p>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
